import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Link2, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { taskService } from '@/services/taskService'
import TaskTypeIcon from './TaskTypeIcon'
import TaskPicker from './TaskPicker'

const LINK_TYPES = [
  { value: 'blocks', label: 'blocks' },
  { value: 'is_blocked_by', label: 'is blocked by' },
  { value: 'relates_to', label: 'relates to' },
  { value: 'duplicates', label: 'duplicates' },
  { value: 'is_duplicated_by', label: 'is duplicated by' },
  { value: 'clones', label: 'clones' },
  { value: 'is_cloned_by', label: 'is cloned by' },
]

// Issue links ("PC-3 blocks PC-7"), grouped by relation. Linked tasks can be in any project
// you can access; links to projects you can't see are not shown.
const TaskLinksPanel = ({ task, projectId, onOpenTask }) => {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [linkType, setLinkType] = useState('relates_to')
  const links = task.links || []

  const groups = links.reduce((acc, link) => {
    (acc[link.label] = acc[link.label] || []).push(link)
    return acc
  }, {})

  const refresh = () => queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])

  const handleAdd = async (target) => {
    try {
      await taskService.addLink(projectId, task.id, linkType, String(target.id))
      refresh()
      setAdding(false)
      toast.success('Link added')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add link')
    }
  }

  const handleRemove = async (link) => {
    try {
      await taskService.removeLink(projectId, task.id, link.id)
      refresh()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove link')
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" /> Linked issues
          {links.length > 0 && <span className="text-muted-foreground font-normal">({links.length})</span>}
        </h3>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdding(v => !v)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Link issue
        </Button>
      </div>

      {adding && (
        <div className="flex items-center gap-2">
          <Select value={linkType} onValueChange={setLinkType}>
            <SelectTrigger className="h-8 w-40 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LINK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <TaskPicker filter={(t) => t.id !== task.id} onSelect={handleAdd} placeholder="Find a ticket in any project…">
            <Button variant="outline" size="sm" className="h-8 flex-1 justify-start font-normal text-muted-foreground">
              Choose a ticket…
            </Button>
          </TaskPicker>
        </div>
      )}

      {Object.entries(groups).map(([label, items]) => (
        <div key={label} className="space-y-1">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <div className="rounded-md border divide-y">
            {items.map(link => (
              <div key={link.id} className="group flex items-center gap-2 px-2.5 py-1.5 text-sm">
                <TaskTypeIcon type={link.task.type} />
                <button type="button" className="font-mono text-[11px] text-muted-foreground hover:text-primary shrink-0" onClick={() => onOpenTask?.(link.task)}>
                  {link.task.key}
                </button>
                <button type="button" onClick={() => onOpenTask?.(link.task)} className={cn('truncate flex-1 text-left hover:text-primary', link.task.status?.category === 'done' && 'line-through text-muted-foreground')}>
                  {link.task.title}
                </button>
                <span className="text-[11px] text-muted-foreground shrink-0">{link.task.status?.name}</span>
                <button type="button" title="Remove link" onClick={() => handleRemove(link)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {!links.length && !adding && <p className="text-xs text-muted-foreground">No linked issues.</p>}
    </div>
  )
}

export default TaskLinksPanel
